/**
 * 风险等级标签
 *
 * 颜色只是辅助：高=红、中=橙、低=蓝、无=绿，同时始终带中文文字与图标，
 * 保证色盲用户与黑白打印（校验报告要打印归档）也能分辨。
 */

import { Tag, Tooltip } from 'antd';
import {
  CheckCircleOutlined,
  ClockCircleOutlined,
  ExclamationCircleOutlined,
  WarningOutlined,
} from '@ant-design/icons';
import { RiskLevel, RISK_LEVEL_LABELS } from '@ilc/shared';

/** 风险等级 → 展示色（同时用于表格、报告、图表） */
export const RISK_COLORS: Record<RiskLevel, string> = {
  [RiskLevel.HIGH]: '#cf1322',
  [RiskLevel.MEDIUM]: '#d46b08',
  [RiskLevel.LOW]: '#0958d9',
  [RiskLevel.NONE]: '#389e0d',
};

const RISK_ICONS: Record<RiskLevel, React.ReactNode> = {
  [RiskLevel.HIGH]: <WarningOutlined />,
  [RiskLevel.MEDIUM]: <ExclamationCircleOutlined />,
  [RiskLevel.LOW]: <ClockCircleOutlined />,
  [RiskLevel.NONE]: <CheckCircleOutlined />,
};

/** 风险等级在业务上的处置口径 —— 直接写在提示里，避免操作人凭感觉判断 */
const RISK_HINTS: Record<RiskLevel, string> = {
  [RiskLevel.HIGH]: '默认禁止流转 T+，如需入账必须走「特例放行」并填写专项审批依据',
  [RiskLevel.MEDIUM]: '只提示不阻断，财务初审人工判断后可继续流转（留痕）',
  [RiskLevel.LOW]: '数据质量问题，人工修正或补录后即可继续',
  [RiskLevel.NONE]: '未发现风险项',
};

export interface RiskTagProps {
  level: RiskLevel | null | undefined;
  /** 是否展示图标（默认展示，满足「不只靠颜色」的要求） */
  showIcon?: boolean;
  /** 是否展示处置口径提示 */
  withHint?: boolean;
  size?: 'default' | 'small';
}

export function RiskTag({
  level,
  showIcon = true,
  withHint = true,
  size = 'default',
}: RiskTagProps): React.ReactElement {
  if (!level) return <Tag>未知</Tag>;
  const label = RISK_LEVEL_LABELS[level] ?? level;
  const color = RISK_COLORS[level] ?? 'default';

  const tag = (
    <Tag
      color={color}
      icon={showIcon ? RISK_ICONS[level] : undefined}
      style={{
        marginInlineEnd: 0,
        fontSize: size === 'small' ? 12 : undefined,
        // 边框 + 文字双重标识，打印为黑白时仍可区分深浅
        borderWidth: level === RiskLevel.HIGH ? 2 : 1,
        fontWeight: level === RiskLevel.HIGH ? 600 : 400,
      }}
    >
      {label}
    </Tag>
  );

  if (!withHint) return tag;
  return <Tooltip title={`${label}：${RISK_HINTS[level]}`}>{tag}</Tooltip>;
}

export default RiskTag;
